/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { CustomXAxisTick } from '../../components/CustomXAxisTick';
import '../../styles/components.css';
import '../../table.css';
import { useAuth } from '../../contexts/AuthContext';

interface ComplianceDocument {
  id: string;
  document_name: string;
  description: string;
  action_type: string;
  responsibility: string;
  step_number: number;
  display_order: number;
  role: string;
  can_company_upload: boolean;
  data_fields?: string[] | string;
  rules?: string[] | string;
  system_trigger?: string;
  visible_ngo_ids?: string[] | null;
  guiding_doc_url?: string | null;
  guiding_doc_filename?: string | null;
  due_date?: string | null;
  target_type?: string | null;
}

interface ComplianceSubmission {
  id: string;
  company_id?: string;
  ngo_id?: string;
  entity_id?: string | null;
  document_repository_id: string;
  assignment_id?: string | null;
  status: string;
  file_name: string;
  file_url: string;
  notes: string | null;
  uploaded_at: string;
  compliance_status?: string | null;
  expiry_date?: string | null;
}

interface DocumentAssignment {
  id: string;
  document_repository_id: string;
  company_id: string;
  entity_id: string | null;
  ngo_id: string | null;
  assigned_by: string;
  status: string;
  due_date: string | null;
  created_at: string;
  is_auto?: boolean;
  document?: ComplianceDocument; // Joined from document_repository
}

interface ComplianceItem {
  document: ComplianceDocument;
  assignment: DocumentAssignment;
  submission: ComplianceSubmission | null;
  status: string;
  ngo_name?: string; // For NGO-uploaded documents
  allSubmissions?: any[]; // All submissions for this document (for NGO documents, shows all NGO uploads)
  request?: any; // For pending document requests
}

interface NGO {
  id: string;
  legal_name: string;
}

const CompanyCompliance: React.FC = () => {
  const { userProfile } = useAuth();
  const companyId = userProfile?.company_id;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checklist, setChecklist] = useState<ComplianceItem[]>([]);
  const [editingItem, setEditingItem] = useState<ComplianceItem | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ngos, setNgos] = useState<NGO[]>([]);
  const [selectedNgoFilter, setSelectedNgoFilter] = useState<string>('all');
  const [activeSection, setActiveSection] = useState<'foundation' | 'company' | 'ngo'>('foundation');
  const [companyEntities, setCompanyEntities] = useState<any[]>([]);
  const [selectedEntityId, setSelectedEntityId] = useState<string>('all');
  const [companyChecklist, setCompanyChecklist] = useState<ComplianceItem[]>([]);
  const [ngoChecklist, setNgoChecklist] = useState<ComplianceItem[]>([]);
  const [documentTemplates, setDocumentTemplates] = useState<ComplianceDocument[]>([]);
  const [documentAssignments, setDocumentAssignments] = useState<DocumentAssignment[]>([]);
  const [companyName, setCompanyName] = useState<string>('');
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string | null>(null);
  const [companyProfile, setCompanyProfile] = useState<any>(null);
  const [signedUrls, setSignedUrls] = useState<Map<string, string>>(new Map());
  const [feedbackModal, setFeedbackModal] = useState<{ uploadId: string; fileName: string; currentFeedback: string | null } | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [showDocumentManagement, setShowDocumentManagement] = useState(false);
  const [showAddDocumentModal, setShowAddDocumentModal] = useState(false);

  // Assignment State
  const [documentAssignmentsMap, setDocumentAssignmentsMap] = useState<Map<string, { entities: Set<string>, ngos: Set<string>, admin?: boolean }>>(new Map());
  const [unsavedChanges, setUnsavedChanges] = useState<Set<string>>(new Set()); // document_id -> has unsaved changes
  const [savingDocumentId, setSavingDocumentId] = useState<string | null>(null); // document_id currently being saved
  const [newDocumentForm, setNewDocumentForm] = useState({
    document_name: '',
    description: '',
    selected_entities: [] as string[],
    selected_ngos: [] as string[],
    assign_to_admin: false,
    due_date: '',
    template_file: null as File | null
  });

  // Helper function to get signed URL for viewing/downloading
  const getSignedUrl = async (fileUrl: string, bucketName: string): Promise<string> => {
    // Check if we already have a signed URL cached
    if (signedUrls.has(fileUrl)) {
      return signedUrls.get(fileUrl)!;
    }

    try {
      // Extract file path from URL
      let filePath = '';

      // Try different URL patterns
      if (fileUrl.includes(`/${bucketName}/`)) {
        const urlParts = fileUrl.split(`/${bucketName}/`);
        if (urlParts.length >= 2) {
          filePath = urlParts[1].split('?')[0]; // Remove query params if any
        }
      } else if (fileUrl.includes(`storage/v1/object/public/${bucketName}/`)) {
        const urlParts = fileUrl.split(`storage/v1/object/public/${bucketName}/`);
        if (urlParts.length >= 2) {
          filePath = urlParts[1].split('?')[0];
        }
      } else if (fileUrl.includes(`storage/v1/object/sign/${bucketName}/`)) {
        const urlParts = fileUrl.split(`storage/v1/object/sign/${bucketName}/`);
        if (urlParts.length >= 2) {
          filePath = urlParts[1].split('?')[0];
        }
      }

      if (!filePath) {
        console.warn('Could not extract file path from URL:', fileUrl);
        return fileUrl;
      }

      const decodedPath = decodeURIComponent(filePath);

      // Get signed URL (valid for 1 hour)
      const { data, error } = await supabase.storage
        .from(bucketName)
        .createSignedUrl(decodedPath, 3600); // 1 hour expiry

      if (error) {
        console.error('Error creating signed URL:', error);
        // If signed URL fails, try public URL as fallback
        const { data: publicData } = supabase.storage
          .from(bucketName)
          .getPublicUrl(filePath);
        return publicData?.publicUrl || fileUrl;
      }

      if (data?.signedUrl) {
        // Cache the signed URL
        setSignedUrls(prev => new Map(prev).set(fileUrl, data.signedUrl));
        return data.signedUrl;
      }

      return fileUrl;
    } catch (err) {
      console.error('Error getting signed URL:', err);
      return fileUrl; // Return original URL as fallback
    }
  };

  // Helper function to handle file download
  const handleDownload = async (fileUrl: string, fileName: string, bucketName: string) => {
    try {
      const signedUrl = await getSignedUrl(fileUrl, bucketName);
      const link = document.createElement('a');
      link.href = signedUrl;
      link.download = fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading file:', err);
      alert('Failed to download file. Please try again.');
    }
  };

  const fetchData = useCallback(async (currentCompanyId: string) => {
    setLoading(true);
    setError(null);
    try {
      // Fetch company name and logo for the admin label and header
      const { data: companyData } = await supabase
        .from('companies')
        .select('name, logo_url, setup_type')
        .eq('id', currentCompanyId)
        .single();
      if (companyData) {
        setCompanyName(companyData.name);
        setCompanyProfile(companyData);
      }

      if (companyData?.logo_url) {
        let filePath = companyData.logo_url;
        if (filePath.includes('/object/public/company-documents/')) {
          filePath = filePath.split('/object/public/company-documents/')[1].split('?')[0];
        } else if (filePath.includes('/company-documents/')) {
          filePath = filePath.split('/company-documents/')[1].split('?')[0];
        }

        const decodedPath = decodeURIComponent(filePath);
        const { data: signedData } = await supabase.storage
          .from('company-documents')
          .createSignedUrl(decodedPath, 3600);
        setCompanyLogoUrl(signedData?.signedUrl || companyData.logo_url);
      }

      // 2. Fetch Assignments for this company (two-step: no join to avoid schema cache issues)
      const { data: assignmentsData, error: assignmentsError } = await supabase
        .from('document_assignments')
        .select('*')
        .eq('company_id', currentCompanyId);

      if (assignmentsError) throw assignmentsError;

      // 3. Fetch ALL compliance templates from the repository
      const { data: repositoryDocs, error: repoError } = await supabase
        .from('document_repository')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (repoError) console.warn('Error fetching document repository:', repoError);

      // 4. Fetch NGOs and Entities BEFORE augmenting assignments
      const { data: companyNGOsData } = await supabase
        .from('ngos')
        .select('id, legal_name')
        .eq('company_id', currentCompanyId)
        .eq('status', 'verified')
        .order('legal_name', { ascending: true });

      const { data: allEntitiesData } = await supabase
        .from('company_entities')
        .select('id, name')
        .eq('company_id', currentCompanyId)
        .order('name', { ascending: true });

      if (allEntitiesData) setCompanyEntities(allEntitiesData);
      const allLinkedNgos = companyNGOsData || [];

      // 5. Implement Hybrid Auto-Assignment Logic
      const augmentedAssignments = [...(assignmentsData || [])];

      repositoryDocs?.forEach((repoDoc: any) => {
        if (repoDoc.role === 'ngo') {
          // Auto-assign to ALL NGOs
          allLinkedNgos.forEach((ngo: any) => {
            const exists = augmentedAssignments.find(a => a.document_repository_id === repoDoc.id && a.ngo_id === ngo.id);
            if (!exists) {
              augmentedAssignments.push({
                id: `auto-ngo-${repoDoc.id}-${ngo.id}`,
                document_repository_id: repoDoc.id,
                company_id: currentCompanyId,
                ngo_id: ngo.id,
                entity_id: null,
                assigned_by: 'system',
                status: 'Pending',
                created_at: new Date().toISOString(),
                due_date: repoDoc.due_date || null,
                is_auto: true
              });
            }
          });
        } else if (repoDoc.role === 'company') {
          if (companyData?.setup_type === 'single') {
            // For a single company with no entities, company docs go to the head office (entity_id = null)
            const exists = augmentedAssignments.find(a => a.document_repository_id === repoDoc.id && !a.entity_id && !a.ngo_id);
            if (!exists) {
              augmentedAssignments.push({
                id: `auto-entity-${repoDoc.id}-single`,
                document_repository_id: repoDoc.id,
                company_id: currentCompanyId,
                ngo_id: null,
                entity_id: null,
                assigned_by: 'system',
                status: 'Pending',
                created_at: new Date().toISOString(),
                due_date: repoDoc.due_date || null,
                is_auto: true
              });
            }
          } else {
            // For complex companies, assign company docs to each business entity (and maybe head office if needed, but here just entities)
            allEntitiesData?.forEach((entity: any) => {
              const exists = augmentedAssignments.find(a => a.document_repository_id === repoDoc.id && a.entity_id === entity.id);
              if (!exists) {
                augmentedAssignments.push({
                  id: `auto-entity-${repoDoc.id}-${entity.id}`,
                  document_repository_id: repoDoc.id,
                  company_id: currentCompanyId,
                  ngo_id: null,
                  entity_id: entity.id,
                  assigned_by: 'system',
                  status: 'Pending',
                  created_at: new Date().toISOString(),
                  due_date: repoDoc.due_date || null,
                  is_auto: true
                });
              }
            });
          }
        } else if (repoDoc.role === 'foundation') {
          // Foundation documents are always assigned to the Corporate / Foundation head office (entity_id = null)
          const exists = augmentedAssignments.find(a => a.document_repository_id === repoDoc.id && !a.entity_id && !a.ngo_id);
          if (!exists) {
            augmentedAssignments.push({
              id: `auto-foundation-${repoDoc.id}`,
              document_repository_id: repoDoc.id,
              company_id: currentCompanyId,
              ngo_id: null,
              entity_id: null,
              assigned_by: 'system',
              status: 'Pending',
              created_at: new Date().toISOString(),
              due_date: repoDoc.due_date || null,
              is_auto: true
            });
          }
        }
      });

      // Step 2b: Collect any additional document_repository_ids from assignments (e.g. custom or other categories)
      const assignedRepoIds = Array.from(new Set(
        augmentedAssignments.map((a: any) => a.document_repository_id).filter(Boolean)
      ));

      const repoDocIds = new Set((repositoryDocs || []).map((d: any) => d.id));
      const missingDocIds = assignedRepoIds.filter(id => !repoDocIds.has(id));

      let allDocs = repositoryDocs || [];
      if (missingDocIds.length > 0) {
        const { data: missingDocs } = await supabase
          .from('document_repository')
          .select('*')
          .in('id', missingDocIds);
        if (missingDocs) {
          allDocs = [...allDocs, ...missingDocs];
        }
      }

      // Build a map for quick lookup
      const docsById = new Map(allDocs.map((d: any) => [d.id, d]));

      // Attach documents to assignments manually
      const enrichedAssignments = augmentedAssignments.map((a: any) => ({
        ...a,
        document: docsById.get(a.document_repository_id) || null,
      }));

      const validAssignments = enrichedAssignments.filter(a => a.document);
      setDocumentAssignments(validAssignments);

      // Set document templates to ALL available documents for manual assignment
      const sortedTemplates = [...allDocs].sort((a, b) => {
        const orderA = a.display_order || a.step_number || 0;
        const orderB = b.display_order || b.step_number || 0;
        return orderA - orderB;
      });
      setDocumentTemplates(sortedTemplates);

      // Extract all unique NGO IDs from assignments
      const ngoIdsFromAssignments = Array.from(new Set(validAssignments.map(a => a.ngo_id).filter(Boolean) as string[]));

      setNgos(allLinkedNgos);

      /* Fetch custom document requests logic removed */

      // 6. Fetch all submissions / uploads for this company and its NGOs
      let submissionsData: any[] = [];
      try {
        const { data: companyUploads } = await supabase
          .from('document_uploads')
          .select('*')
          .eq('company_id', currentCompanyId)
          .eq('status', 'active')
          .order('uploaded_at', { ascending: false });

        // Use allLinkedNgos for fetching NGO uploads
        let ngoUploads: any[] = [];
        if (allLinkedNgos.length > 0) {
          const { data: ngoUploadsData, error: ngoError } = await supabase
            .from('document_uploads')
            .select('*')
            .in('ngo_id', allLinkedNgos.map((n: any) => n.id))
            .eq('status', 'active')
            .order('uploaded_at', { ascending: false });

          if (!ngoError && ngoUploadsData) {
            ngoUploads = ngoUploadsData;
          }
        }
        submissionsData = [...(companyUploads || []), ...ngoUploads];
      } catch (err: any) {
        console.warn('Error fetching submissions:', err);
      }

      // 7. Create Checklist Items based on assignments instead of templates
      const companyChecklistItems: ComplianceItem[] = [];
      const ngoChecklistItems: ComplianceItem[] = [];

      validAssignments.forEach((assignment: any) => {
        const doc = assignment.document;

        // Find uploads for this assignment
        // Match strictly by assignment_id since all uploads now link to a real assignment (JIT created)
        const assignmentUploads = submissionsData.filter((s: any) => {
          if (s.assignment_id && s.assignment_id === assignment.id) return true;

          // Legacy / Robust fallback for explicitly orphaned uploads (manual mismatch)
          if (assignment.ngo_id) {
            return s.document_repository_id === doc.id && s.ngo_id === assignment.ngo_id;
          }
          return false;
        });

        const latestSubmission = assignmentUploads.length > 0
          ? assignmentUploads.sort((a, b) => new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime())[0]
          : null;

        const item: ComplianceItem = {
          document: doc,
          assignment: assignment,
          submission: latestSubmission,
          status: latestSubmission
            ? (['Yes', 'Completed'].includes(latestSubmission.compliance_status) ? 'Approved' :
              (['No', 'In Progress'].includes(latestSubmission.compliance_status) ? 'Rejected' :
                latestSubmission.compliance_status === 'Pending' ? 'Under Review' :
                  (latestSubmission.status || 'Submitted')))
            : 'Not Submitted',
          allSubmissions: assignmentUploads
        };

        if (assignment.ngo_id) {
          item.ngo_name = allLinkedNgos.find((n: any) => n.id === assignment.ngo_id)?.legal_name;
          ngoChecklistItems.push(item);
        } else {
          // Check if this latest submission is "Shared" (from another entity)
          if (latestSubmission && latestSubmission.entity_id !== assignment.entity_id) {
            const sourceEntity = latestSubmission.entity_id
              ? companyEntities.find(e => e.id === latestSubmission.entity_id)?.name || 'Other Entity'
              : (companyName || 'Foundation');
            item.status = `Shared (${sourceEntity})`;
          }
          companyChecklistItems.push(item);
        }
      });

      // Also include NGO uploads that don't have a matching assignment
      // (e.g. NGO uploaded a doc from their tracker without a formal assignment)
      const coveredNgoUploadIds = new Set(
        ngoChecklistItems.flatMap(item => (item.allSubmissions || []).map((s: any) => s.id))
      );

      // Find NGO uploads not covered by any assignment
      const orphanNgoUploads = submissionsData.filter((s: any) =>
        s.ngo_id && !coveredNgoUploadIds.has(s.id)
      );

      // Group orphan uploads by document_repository_id + ngo_id
      const orphanGroupKey = (s: any) => `${s.document_repository_id}::${s.ngo_id}`;
      const orphanGroups = new Map<string, any[]>();
      orphanNgoUploads.forEach((s: any) => {
        const key = orphanGroupKey(s);
        if (!orphanGroups.has(key)) orphanGroups.set(key, []);
        orphanGroups.get(key)!.push(s);
      });

      Array.from(orphanGroups.values()).forEach((uploads) => {
        uploads.sort((a: any, b: any) => new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime());
        const latest = uploads[0];
        const ngoName = allLinkedNgos.find((n: any) => n.id === latest.ngo_id)?.legal_name;
        // Try to find document info from documents we already fetched
        let docInfo = docsById.get(latest.document_repository_id);
        if (!docInfo) {
          // Smart resolution: try finding by name match in the repository
          const uploadName = (latest.file_name || '').toLowerCase();
          const nameMatch = Array.from(docsById.values()).find((d: any) => {
            const templateName = (d as any).document_name.toLowerCase();
            return uploadName.includes(templateName) || templateName.includes(uploadName.split('_')[0]);
          });

          if (nameMatch) {
            docInfo = nameMatch;
          } else {
            docInfo = {
              id: latest.document_repository_id || latest.id,
              document_name: latest.file_name || 'Uploaded Document',
              description: '',
              action_type: 'Upload',
              responsibility: 'NGO',
              step_number: 999,
              display_order: 999,
              role: 'ngo'
            };
          }
        }

        const orphanItem: ComplianceItem = {
          document: docInfo as any,
          assignment: {
            id: `orphan-${latest.id}`,
            document_repository_id: latest.document_repository_id,
            company_id: currentCompanyId,
            entity_id: null,
            ngo_id: latest.ngo_id,
            assigned_by: '',
            status: 'submitted',
            due_date: null,
            created_at: latest.uploaded_at,
          },
          submission: latest,
          status: 'Unassigned',
          ngo_name: ngoName,
          allSubmissions: uploads,
        };
        ngoChecklistItems.push(orphanItem);
      });

      // 9. Recover Company Orphans
      const coveredCompanyUploadIds = new Set(
        companyChecklistItems.flatMap(item => (item.allSubmissions || []).map((s: any) => s.id))
      );

      const orphanCompanyUploads = submissionsData.filter((s: any) =>
        !s.ngo_id && s.company_id === currentCompanyId && !coveredCompanyUploadIds.has(s.id)
      );

      const companyOrphanGroups = new Map<string, any[]>();
      orphanCompanyUploads.forEach((s: any) => {
        const key = `${s.document_repository_id}::${s.entity_id || 'admin'}`;
        if (!companyOrphanGroups.has(key)) companyOrphanGroups.set(key, []);
        companyOrphanGroups.get(key)!.push(s);
      });

      Array.from(companyOrphanGroups.values()).forEach((uploads) => {
        uploads.sort((a: any, b: any) => new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime());
        const latest = uploads[0];
        let docInfo = docsById.get(latest.document_repository_id);
        if (!docInfo) {
          // Smart resolution: try finding by name match in the repository
          const uploadName = (latest.file_name || '').toLowerCase();
          const nameMatch = Array.from(docsById.values()).find(d => {
            const templateName = d.document_name.toLowerCase();
            return uploadName.includes(templateName) || templateName.includes(uploadName.split('_')[0]);
          });

          if (nameMatch) {
            docInfo = nameMatch;
          } else {
            docInfo = {
              id: latest.document_repository_id || latest.id,
              document_name: latest.file_name || 'Uploaded Document',
              description: '',
              action_type: 'Upload',
              responsibility: 'Company',
              step_number: 999,
              display_order: 999,
              role: 'company',
              can_company_upload: true
            };
          }
        }

        const orphanItem: ComplianceItem = {
          document: docInfo,
          assignment: {
            id: `orphan-company-${latest.id}`,
            document_repository_id: latest.document_repository_id,
            company_id: currentCompanyId,
            entity_id: latest.entity_id || null,
            ngo_id: null,
            assigned_by: '',
            status: 'submitted',
            due_date: null,
            created_at: latest.uploaded_at,
          },
          submission: latest,
          status: 'Unassigned',
          allSubmissions: uploads,
        };
        companyChecklistItems.push(orphanItem);
      });

      const sortDocs = (a: ComplianceItem, b: ComplianceItem) => {
        const aDue = a.assignment.due_date || a.document.due_date;
        const bDue = b.assignment.due_date || b.document.due_date;
        if (aDue && bDue) {
          return new Date(aDue).getTime() - new Date(bDue).getTime();
        }
        if (aDue) return -1;
        if (bDue) return 1;
        return (a.document.display_order || 0) - (b.document.display_order || 0);
      };

      /* Requests logic removed as the user requested removal of Request Doc for NGO */

      companyChecklistItems.sort(sortDocs);
      ngoChecklistItems.sort(sortDocs);

      setCompanyChecklist(companyChecklistItems);
      setNgoChecklist(ngoChecklistItems);
      setChecklist([...companyChecklistItems, ...ngoChecklistItems]);



    } catch (err: any) {
      console.error("Fetch Data Error:", err);
      setError(err.message || "An error occurred while fetching compliance data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (companyId) {
      fetchData(companyId);
    }
  }, [companyId, fetchData]);

  const filteredFoundationChecklist = useMemo(() => {
    return companyChecklist.filter(item => item.assignment.entity_id === null);
  }, [companyChecklist]);

  const filteredCompanyChecklist = useMemo(() => {
    if (companyProfile?.setup_type === 'single') {
        return companyChecklist;
    }
    let docs = companyChecklist.filter(item => item.assignment.entity_id !== null);
    if (selectedEntityId !== 'all') {
        docs = docs.filter(item => item.assignment.entity_id === selectedEntityId);
    }
    return docs;
  }, [companyChecklist, selectedEntityId, companyProfile]);

  // NGO checklist is simple now: we have one item per assigned document per NGO
  const filteredNgoChecklist = useMemo(() => {
    if (selectedNgoFilter === 'all') {
      return ngoChecklist;
    }
    return ngoChecklist.filter(item => item.assignment.ngo_id === selectedNgoFilter);
  }, [ngoChecklist, selectedNgoFilter]);

  // Dashboard analytics
  const dashboardData = useMemo(() => {
    const totalCompanyDocs = companyChecklist.length; // Total assignments for all entities
    const companyUploaded = companyChecklist.filter(item => item.submission).length;

    const totalNgoDocs = ngoChecklist.length; // Total assignments across all NGOs
    const ngoSubmitted = ngoChecklist.filter(item => item.submission).length;
    const totalNgoRows = totalNgoDocs; // Same thing

    // Chart data for Total vs Uploaded (company only) split by entity
    const foundationAssignments = companyChecklist.filter(item => item.assignment.entity_id === null);
    const foundationUploaded = foundationAssignments.filter(item => item.submission).length;

    const chartData = [
      {
        name: companyProfile?.setup_type === 'single' ? (companyProfile?.name || 'Company') : 'Foundation',
        'Total Documents': foundationAssignments.length,
        'Uploaded': foundationUploaded,
        'Not Uploaded': Math.max(0, foundationAssignments.length - foundationUploaded)
      }
    ];

    if (companyProfile?.setup_type !== 'single') {
      companyEntities.forEach(entity => {
        const entityAssignments = companyChecklist.filter(item => item.assignment.entity_id === entity.id);
        const entityUploaded = entityAssignments.filter(item => item.submission).length;

        chartData.push({
          name: entity.name,
          'Total Documents': entityAssignments.length,
          'Uploaded': entityUploaded,
          'Not Uploaded': Math.max(0, entityAssignments.length - entityUploaded)
        });
      });
    }

    // NGO-wise breakdown for the second chart
    const ngoWiseData = ngos.map(ngo => {
      const ngoAssignments = ngoChecklist.filter(item => item.assignment.ngo_id === ngo.id);
      const uploaded = ngoAssignments.filter(item => item.submission).length;
      return {
        name: ngo.legal_name.length > 15 ? ngo.legal_name.substring(0, 15) + '...' : ngo.legal_name,
        'Total Documents': ngoAssignments.length,
        'Uploaded': uploaded
      };
    });

    return {
      totalCompanyDocs,
      totalNgoDocs,
      companyUploaded,
      ngoUploaded: ngoSubmitted,
      totalNgoRows,
      ngoSubmitted,
      chartData,
      ngoWiseData
    };
  }, [companyChecklist, ngoChecklist, companyEntities, ngos]);

  if (loading) {
    return (
      <div
        style={{
          minHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem'
        }}
      >
        <div className="spinner"></div>
        <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>Loading compliance data...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="compliance-container">
        <div style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          <div>
            <h1 style={{
              fontSize: '2rem',
              fontWeight: 700,
              color: 'var(--text)',
              marginBottom: '0.5rem',
              letterSpacing: '-0.5px'
            }}>
              Compliance Management
            </h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', margin: 0, marginBottom: '1rem' }}>
              Track and manage all compliance requirements for your organization.
            </p>
            <div style={{
              backgroundColor: '#eff6ff',
              border: '1px solid #bfdbfe',
              color: '#1e3a8a',
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span style={{ fontSize: '1.25rem' }}>💡</span>
              <strong>Quick Guide:</strong> You can upload your Foundation and Company documents, or review and approve NGO uploaded documents using the tabs below.
            </div>
          </div>
        </div>

        {error && (
          <div className="toast toast-error" style={{ marginBottom: '1.5rem' }}>
            {error}
          </div>
        )}



        {/* Compliance Checklist */}
        <div className="card" style={{ padding: '2rem' }}>
          {/* Section Tabs */}
          <div className="compliance-tab-bar" style={{ marginBottom: '2rem', borderBottom: '2px solid var(--border)', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <button
              onClick={() => setActiveSection('foundation')}
              style={{
                padding: '0.75rem 1.5rem',
                border: 'none',
                background: 'transparent',
                borderBottom: activeSection === 'foundation' ? '2px solid var(--primary)' : '2px solid transparent',
                color: activeSection === 'foundation' ? 'var(--primary)' : 'var(--text-secondary)',
                fontWeight: activeSection === 'foundation' ? '600' : '400',
                cursor: 'pointer',
                textTransform: 'capitalize',
                marginBottom: '-2px'
              }}
            >
              {companyProfile?.setup_type === 'single' ? 'Company Documents' : 'Foundation Documents'} ({filteredFoundationChecklist.length})
            </button>

            {companyProfile?.setup_type !== 'single' && (
              <button
                onClick={() => setActiveSection('company')}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  background: 'transparent',
                  borderBottom: activeSection === 'company' ? '2px solid var(--primary)' : '2px solid transparent',
                  color: activeSection === 'company' ? 'var(--primary)' : 'var(--text-secondary)',
                  fontWeight: activeSection === 'company' ? '600' : '400',
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                  marginBottom: '-2px'
                }}
              >
                Entity Documents ({filteredCompanyChecklist.length})
              </button>
            )}




            <button
              onClick={() => setActiveSection('ngo')}
              style={{
                padding: '0.75rem 1.5rem',
                border: 'none',
                background: 'transparent',
                borderBottom: activeSection === 'ngo' ? '2px solid var(--primary)' : '2px solid transparent',
                color: activeSection === 'ngo' ? 'var(--primary)' : 'var(--text-secondary)',
                fontWeight: activeSection === 'ngo' ? '600' : '400',
                cursor: 'pointer',
                textTransform: 'capitalize',
                marginBottom: '-2px'
              }}
            >
              NGO Documents ({filteredNgoChecklist.length})
            </button>
            {(activeSection === 'company' || activeSection === 'foundation') && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowDocumentManagement(!showDocumentManagement);
                  if (showDocumentManagement) {
                    setActiveSection('foundation');
                  }
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  background: showDocumentManagement ? 'var(--primary)' : 'transparent',
                  borderBottom: showDocumentManagement ? '2px solid var(--primary)' : '2px solid transparent',
                  color: showDocumentManagement ? 'white' : 'var(--text-secondary)',
                  fontWeight: showDocumentManagement ? '600' : '400',
                  cursor: 'pointer',
                  marginBottom: '-2px',
                  marginLeft: 'auto',
                  borderRadius: showDocumentManagement ? '8px 8px 0 0' : '0'
                }}
              >
                📋 Manage Documents
              </button>
            )}

            {/* Entity filter visible on Company section */}
            {activeSection === 'company' && !showDocumentManagement && companyProfile?.setup_type !== 'single' && (
              <div style={{ marginLeft: 'auto', background: 'var(--bg-light)', padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid var(--primary-light)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <label style={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: 'var(--text-secondary)',
                    whiteSpace: 'nowrap'
                  }}>
                    Filter by Entity:
                  </label>
                  <select
                    value={selectedEntityId}
                    onChange={(e) => setSelectedEntityId(e.target.value)}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      background: 'white',
                      cursor: 'pointer',
                      fontWeight: 600,
                      color: 'var(--text)'
                    }}
                  >
                    <option value="all">Show All</option>
                    {companyEntities.map(entity => (
                      <option key={entity.id} value={entity.id}>{entity.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* NGO filter only visible on NGO section */}
            {activeSection === 'ngo' && ngos.length > 0 && (
              <div style={{ marginLeft: 'auto', background: 'var(--bg-light)', padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid var(--primary-light)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <label style={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: 'var(--text-secondary)',
                    whiteSpace: 'nowrap'
                  }}>
                    Filter by NGO:
                  </label>
                  <select
                    value={selectedNgoFilter}
                    onChange={(e) => setSelectedNgoFilter(e.target.value)}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: '0.875rem',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      minWidth: '200px',
                      fontWeight: 600,
                      color: 'var(--text)'
                    }}
                  >
                    <option value="all">All NGOs</option>
                    {ngos.map(ngo => (
                      <option key={ngo.id} value={ngo.id}>{ngo.legal_name}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Document Management Section */}
          {showDocumentManagement ? (
            <div className="card" style={{ padding: '2rem', marginBottom: '2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <div>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)', margin: 0, marginBottom: '0.5rem' }}>
                    Document Management
                  </h2>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>
                    Manage manual document assignments for your Corporate office, Business Units, and NGOs
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setShowAddDocumentModal(true);
                      setNewDocumentForm({
                        document_name: '',
                        description: '',
                        selected_entities: [],
                        selected_ngos: [],
                        assign_to_admin: false,
                        due_date: '',
                        template_file: null
                      });
                    }}
                  >
                    + Add Custom Document
                  </button>
                </div>
              </div>

              {/* Template Assignments Controls */}
              <div style={{ display: 'grid', gap: '1.5rem' }}>
                {documentTemplates.map((template) => {
                  // Get assignments from local state (for immediate UI updates) or fallback to assignments fetch
                  const localAssignments = documentAssignmentsMap.get(template.id);

                  // Initialize assigned entities and ngos either from local state or the assignments array
                  let assignedEntityIds = new Set<string>();
                  let assignedNgoIds = new Set<string>();
                  let isAssignedToAdmin = false;

                  if (localAssignments) {
                    assignedEntityIds = localAssignments.entities;
                    assignedNgoIds = localAssignments.ngos;
                    isAssignedToAdmin = localAssignments.admin || false;
                  } else {
                    documentAssignments
                      .filter(a => a.document_repository_id === template.id)
                      .forEach(a => {
                        if (a.entity_id) assignedEntityIds.add(a.entity_id);
                        if (a.ngo_id) assignedNgoIds.add(a.ngo_id);
                        if (!a.entity_id && !a.ngo_id) isAssignedToAdmin = true;
                      });
                  }

                  const hasEntityAssignments = assignedEntityIds.size > 0;
                  const hasNgoAssignments = assignedNgoIds.size > 0;
                  const totalAssignmentsCount = assignedEntityIds.size + assignedNgoIds.size + (isAssignedToAdmin ? 1 : 0);
                  const isVisibleToAllNgos = assignedNgoIds.size === ngos.length && ngos.length > 0;
                  const isVisibleToAllEntities = assignedEntityIds.size === companyEntities.length && companyEntities.length > 0;

                  // Create a key to force re-render
                  const visibilityKey = `${template.id}-${Array.from(assignedEntityIds).join(',')}-${Array.from(assignedNgoIds).join(',')}-${isAssignedToAdmin}`;

                  return (
                    <div key={visibilityKey} className="card" style={{ padding: '1.5rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                        <div style={{ flex: 1 }}>
                          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text)', margin: 0, marginBottom: '0.5rem' }}>
                            {template.document_name}
                          </h3>
                          {template.description && (
                            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0 }}>
                              {template.description}
                            </p>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            backgroundColor: totalAssignmentsCount > 0 ? '#10b98115' : '#f59e0b15',
                            color: totalAssignmentsCount > 0 ? '#10b981' : '#f59e0b',
                            fontWeight: '600'
                          }}>
                            {totalAssignmentsCount === 0 ? 'Not Assigned' : `Assigned to ${totalAssignmentsCount} targets`}
                          </span>
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Delete "${template.document_name}"? This will remove all assignments for this document.`)) return;
                              try {
                                // Delete all assignments first
                                await supabase
                                  .from('document_assignments')
                                  .delete()
                                  .eq('document_repository_id', template.id)
                                  .eq('company_id', companyId);
                                // Delete the document itself
                                await supabase
                                  .from('document_repository')
                                  .delete()
                                  .eq('id', template.id);
                                // Remove from local state
                                setDocumentTemplates(prev => prev.filter(t => t.id !== template.id));
                                setDocumentAssignments(prev => prev.filter(a => a.document_repository_id !== template.id));
                              } catch (err: any) {
                                alert('Failed to delete document: ' + (err.message || 'Unknown error'));
                              }
                            }}
                            style={{
                              padding: '0.35rem 0.65rem',
                              background: '#fee2e2',
                              color: '#ef4444',
                              border: '1px solid #fca5a5',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              whiteSpace: 'nowrap'
                            }}
                            title="Delete this document"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>

                      {/* Assignment Controls */}
                      <div style={{ marginTop: '1rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid var(--border)' }}>
                        <strong style={{ fontSize: '0.875rem', display: 'block', marginBottom: '0.75rem' }}>
                          Assign Document To:
                        </strong>

                        {/* Assign to Admin */}
                        <div style={{ marginBottom: '1.5rem' }}>
                          <strong style={{ fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
                            Corporate / Primary Account
                          </strong>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              padding: '0.5rem',
                              background: isAssignedToAdmin ? '#eff6ff' : 'white',
                              border: isAssignedToAdmin ? '1px solid #3b82f6' : '1px solid #e5e7eb',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              width: 'fit-content'
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              if (!companyId) return;

                              setDocumentAssignmentsMap(prev => {
                                const newMap = new Map(prev);
                                const current = newMap.get(template.id) || { entities: new Set(assignedEntityIds), ngos: new Set(assignedNgoIds), admin: isAssignedToAdmin };
                                newMap.set(template.id, { ...current, admin: !current.admin });
                                return newMap;
                              });

                              setUnsavedChanges(prev => new Set(prev).add(template.id));
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isAssignedToAdmin}
                              onChange={() => { }} // Handled by onClick on parent div
                              onClick={(e) => e.stopPropagation()}
                              disabled={isSubmitting}
                              style={{ cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                            />
                            <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{companyName || (companyProfile?.setup_type === 'single' ? 'Primary' : 'Corporate Foundation Account')}</span>
                          </div>
                        </div>

                        {/* Assign to Entities - Only show if not single company */}
                        {companyProfile?.setup_type !== 'single' && (
                          <div style={{ marginBottom: '1.5rem' }}>
                            <strong style={{ fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
                              Secondary Business Units
                            </strong>
                            {companyEntities.length === 0 ? (
                              <p style={{ fontSize: '0.875rem', color: 'var(--muted)', margin: 0 }}>
                                No internal entities found.
                              </p>
                            ) : (
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.5rem' }}>
                                {companyEntities.map(entity => {
                                  const isAssigned = assignedEntityIds.has(entity.id);
                                  return (
                                    <div
                                      key={`entity-${entity.id}`}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        padding: '0.5rem',
                                        background: isAssigned ? '#eff6ff' : 'white',
                                        border: isAssigned ? '1px solid #3b82f6' : '1px solid #e5e7eb',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        if (!companyId) return;

                                        setDocumentAssignmentsMap(prev => {
                                          const newMap = new Map(prev);
                                          const current = newMap.get(template.id) || { entities: new Set(), ngos: new Set(assignedNgoIds), admin: isAssignedToAdmin };

                                          // Initialize entities if they somehow weren't populated
                                          const newEntities = new Set(current.entities);
                                          if (newEntities.size === 0 && assignedEntityIds.size > 0) {
                                            assignedEntityIds.forEach(id => newEntities.add(id));
                                          }

                                          if (isAssigned) {
                                            newEntities.delete(entity.id);
                                          } else {
                                            newEntities.add(entity.id);
                                          }

                                          newMap.set(template.id, { ...current, entities: newEntities });
                                          return newMap;
                                        });

                                        setUnsavedChanges(prev => new Set(prev).add(template.id));
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isAssigned}
                                        onChange={() => { }} // Handled by onClick on parent div
                                        onClick={(e) => e.stopPropagation()}
                                        disabled={isSubmitting}
                                        style={{ cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                                      />
                                      <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{entity.name}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Assign to NGOs */}
                        <div>
                          <strong style={{ fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
                            External NGOs
                          </strong>
                          {ngos.length === 0 ? (
                            <p style={{ fontSize: '0.875rem', color: 'var(--muted)', margin: 0 }}>
                              No NGOs linked to your company yet.
                            </p>
                          ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.5rem' }}>
                              {ngos.map(ngo => {
                                const isAssigned = assignedNgoIds.has(ngo.id);
                                return (
                                  <div
                                    key={`ngo-${ngo.id}`}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.5rem',
                                      padding: '0.5rem',
                                      background: isAssigned ? '#f0fdf4' : 'white',
                                      border: isAssigned ? '1px solid #10b981' : '1px solid #e5e7eb',
                                      borderRadius: '4px',
                                      cursor: 'pointer'
                                    }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      if (!companyId) return;

                                      setDocumentAssignmentsMap(prev => {
                                        const newMap = new Map(prev);
                                        const current = newMap.get(template.id) || { entities: new Set(assignedEntityIds), ngos: new Set(), admin: isAssignedToAdmin };

                                        // Initialize ngos if they somehow weren't populated
                                        const newNgos = new Set(current.ngos);
                                        if (newNgos.size === 0 && assignedNgoIds.size > 0) {
                                          assignedNgoIds.forEach(id => newNgos.add(id));
                                        }

                                        if (isAssigned) {
                                          newNgos.delete(ngo.id);
                                        } else {
                                          newNgos.add(ngo.id);
                                        }

                                        newMap.set(template.id, { ...current, ngos: newNgos });
                                        return newMap;
                                      });

                                      setUnsavedChanges(prev => new Set(prev).add(template.id));
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isAssigned}
                                      onChange={() => { }} // Handled by onClick on parent div
                                      onClick={(e) => e.stopPropagation()}
                                      disabled={isSubmitting}
                                      style={{ cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                                    />
                                    <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{ngo.legal_name}</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Save Button - Only show if there are unsaved changes */}
                        {unsavedChanges.has(template.id) && (
                          <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                              className="btn btn-primary"
                              onClick={async () => {
                                if (!companyId) return;
                                setSavingDocumentId(template.id);
                                try {
                                  const localAssignments = documentAssignmentsMap.get(template.id);
                                  if (!localAssignments) return;

                                  // Get existing assignments for this document/company
                                  const existingAssignments = documentAssignments.filter(a => a.document_repository_id === template.id && a.company_id === companyId);

                                  // Identify what to keep, add, and delete
                                  const targetEntities = localAssignments.entities;
                                  const targetNgos = localAssignments.ngos;
                                  const targetAdmin = !!localAssignments.admin;

                                  const assignmentsToDelete: string[] = [];
                                  const assignmentsToAdd: any[] = [];

                                  // Check existing for deletions
                                  existingAssignments.forEach(ea => {
                                    if (ea.entity_id) {
                                      if (!targetEntities.has(ea.entity_id)) assignmentsToDelete.push(ea.id);
                                    } else if (ea.ngo_id) {
                                      if (!targetNgos.has(ea.ngo_id)) assignmentsToDelete.push(ea.id);
                                    } else {
                                      // Admin assignment
                                      if (!targetAdmin) assignmentsToDelete.push(ea.id);
                                    }
                                  });

                                  // Check targets for additions
                                  targetEntities.forEach(eid => {
                                    if (!existingAssignments.find(ea => ea.entity_id === eid)) {
                                      assignmentsToAdd.push({
                                        document_repository_id: template.id,
                                        company_id: companyId,
                                        entity_id: eid,
                                        ngo_id: null,
                                        status: 'Pending'
                                      });
                                    }
                                  });

                                  targetNgos.forEach(nid => {
                                    if (!existingAssignments.find(ea => ea.ngo_id === nid)) {
                                      assignmentsToAdd.push({
                                        document_repository_id: template.id,
                                        company_id: companyId,
                                        entity_id: null,
                                        ngo_id: nid,
                                        status: 'Pending'
                                      });
                                    }
                                  });

                                  if (targetAdmin && !existingAssignments.find(ea => !ea.entity_id && !ea.ngo_id)) {
                                    assignmentsToAdd.push({
                                      document_repository_id: template.id,
                                      company_id: companyId,
                                      entity_id: null,
                                      ngo_id: null,
                                      status: 'Pending'
                                    });
                                  }

                                  // Execute deletions
                                  if (assignmentsToDelete.length > 0) {
                                    const { error: deleteError } = await supabase
                                      .from('document_assignments')
                                      .delete()
                                      .in('id', assignmentsToDelete);
                                    if (deleteError) throw deleteError;
                                  }

                                  // Execute additions
                                  if (assignmentsToAdd.length > 0) {
                                    const { error: insertError } = await supabase
                                      .from('document_assignments')
                                      .insert(assignmentsToAdd);
                                    if (insertError) throw insertError;
                                  }

                                  // Remove from unsaved changes
                                  setUnsavedChanges(prev => {
                                    const newSet = new Set(prev);
                                    newSet.delete(template.id);
                                    return newSet;
                                  });

                                  alert('Assignments saved successfully!');

                                  // Refresh data to ensure everything is in sync
                                  if (companyId) await fetchData(companyId);
                                } catch (err: any) {
                                  console.error('Error saving assignments:', err);
                                  alert(err.message || 'Failed to save assignments');
                                } finally {
                                  setSavingDocumentId(null);
                                }
                              }}
                              disabled={savingDocumentId === template.id}
                              style={{
                                backgroundColor: '#10b981',
                                color: 'white',
                                border: 'none',
                                fontSize: '0.875rem',
                                padding: '0.5rem 1rem'
                              }}
                            >
                              {savingDocumentId === template.id ? 'Saving...' : '💾 Save Changes'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (activeSection === 'company' || activeSection === 'foundation') ? (
            (activeSection === 'company' ? filteredCompanyChecklist : filteredFoundationChecklist).length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '4rem 2rem',
                background: 'var(--background)',
                borderRadius: 'var(--radius-md)',
                border: '2px dashed var(--border-light)'
              }}>
                <div style={{
                  fontSize: '3rem',
                  marginBottom: '1rem',
                  opacity: 0.3
                }}>📋</div>
                <p style={{
                  color: 'var(--text-secondary)',
                  fontSize: '1rem',
                  marginBottom: '0.5rem',
                  fontWeight: 600
                }}>
                  No company compliance documents found.
                </p>
                <p style={{
                  color: 'var(--muted)',
                  fontSize: '0.875rem'
                }}>
                  Compliance templates will appear here once they are created in the system.
                </p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '1.5rem' }}>
                {(activeSection === 'company' ? filteredCompanyChecklist : filteredFoundationChecklist).map((item) => {
                  const dataFields = typeof item.document.data_fields === 'string'
                    ? JSON.parse(item.document.data_fields || '[]')
                    : (item.document.data_fields || []);
                  const rules = typeof item.document.rules === 'string'
                    ? JSON.parse(item.document.rules || '[]')
                    : (item.document.rules || []);

                  return (
                    <div key={item.assignment.id} className="card" style={{ position: 'relative', paddingLeft: '3rem' }}>
                      {/* Step Number Badge */}
                      <div style={{
                        position: 'absolute',
                        left: '1rem',
                        top: '1rem',
                        width: '2rem',
                        height: '2rem',
                        borderRadius: '50%',
                        background: 'var(--primary)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '600',
                        fontSize: '0.875rem'
                      }}>
                        {item.document.display_order || item.document.step_number}
                      </div>

                      {/* Document Header */}
                      <div style={{ marginBottom: '0.75rem' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                          <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: 'var(--text)' }}>
                            {item.document.document_name}
                          </h3>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            backgroundColor: '#3b82f615',
                            color: '#3b82f6',
                            fontWeight: '600'
                          }}>
                            Company
                          </span>

                          {/* Entity / Admin label */}
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            backgroundColor: item.assignment.id.startsWith('orphan-') ? '#9ca3af15' : '#f59e0b15',
                            color: item.assignment.id.startsWith('orphan-') ? '#6b7280' : '#b45309',
                            fontWeight: '600'
                          }}>
                            {item.assignment.id.startsWith('orphan-') ? 'Unassigned / Historical' : (item.assignment.entity_id
                              ? (companyEntities.find(e => e.id === item.assignment.entity_id)?.name || 'Entity')
                              : (companyName || 'Foundation')
                            )}
                          </span>

                          <span style={{
                            padding: '0.375rem 0.875rem',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            display: 'inline-block',
                            backgroundColor: item.status === 'Approved' || item.status === 'Completed' ? '#10b98115' :
                              item.status === 'Rejected' ? '#ef444415' :
                                item.status.includes('Shared') ? '#ecfdff' :
                                  item.status === 'Unassigned' ? '#f3f4f6' :
                                    item.status === 'Pending Review' || item.status === 'Under Review' ? '#f59e0b15' :
                                      item.status === 'Submitted' ? '#3b82f615' : '#6b728015',
                            color: item.status === 'Approved' || item.status === 'Completed' ? '#10b981' :
                              item.status === 'Rejected' ? '#ef4444' :
                                item.status.includes('Shared') ? '#0891b2' :
                                  item.status === 'Unassigned' ? '#4b5563' :
                                    item.status === 'Pending Review' || item.status === 'Under Review' ? '#f59e0b' :
                                      item.status === 'Submitted' ? '#3b82f6' : '#6b7280'
                          }}>
                            {item.status}
                          </span>
                        </div>
                      </div>

                      {/* Due Date Display */}
                      {item.document.due_date && (
                        <div style={{ marginBottom: '0.75rem', marginTop: '-0.5rem' }}>
                          <span style={{
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            color: new Date(item.document.due_date) < new Date() ? '#ef4444' : '#f59e0b',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            📅 Due: {new Date(item.document.due_date).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}
                            {new Date(item.document.due_date) < new Date() && <span style={{ fontSize: '0.7rem' }}>(Overdue)</span>}
                          </span>
                        </div>
                      )}

                      {/* Description */}
                      {item.document.description && (
                        <p style={{ color: 'var(--text-secondary)', marginBottom: '0.75rem', lineHeight: '1.6' }}>
                          {item.document.description}
                        </p>
                      )}

                      {/* Guiding Document */}
                      {item.document.guiding_doc_url && (
                        <div style={{ marginBottom: '0.75rem', padding: '0.75rem', background: '#ecfdf5', borderRadius: '8px', border: '1px solid #10b981' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <strong style={{ fontSize: '0.875rem', color: '#065f46' }}>📄 Guiding Document:</strong>
                              <p style={{ fontSize: '0.75rem', color: '#047857', margin: '0.25rem 0 0 0' }}>
                                {item.document.guiding_doc_filename || 'Guideline Document'}
                              </p>
                            </div>
                            <a
                              href={item.document.guiding_doc_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm"
                              style={{
                                fontSize: '0.8rem',
                                backgroundColor: '#10b981',
                                color: 'white',
                                border: 'none'
                              }}
                            >
                              View Guide
                            </a>
                          </div>
                        </div>
                      )}

                      {/* Responsibility */}
                      {item.document.responsibility && (
                        <div style={{ marginBottom: '0.75rem' }}>
                          <strong style={{ fontSize: '0.875rem' }}>Responsibility:</strong>
                          <span style={{ marginLeft: '0.5rem', color: 'var(--primary)', fontWeight: '500' }}>
                            {item.document.responsibility}
                          </span>
                        </div>
                      )}

                      {/* Data Fields */}
                      {Array.isArray(dataFields) && dataFields.length > 0 && (
                        <div style={{ marginBottom: '0.75rem' }}>
                          <strong style={{ fontSize: '0.875rem' }}>Data Fields:</strong>
                          <ul style={{ margin: '0.5rem 0 0 1.5rem', padding: 0, listStyle: 'disc' }}>
                            {dataFields.map((field: string, idx: number) => (
                              <li key={idx} style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                                {field}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Rules */}
                      {Array.isArray(rules) && rules.length > 0 && (
                        <div style={{ marginBottom: '0.75rem', padding: '0.75rem', background: '#fef3c7', borderRadius: '8px', border: '1px solid #f59e0b' }}>
                          <strong style={{ fontSize: '0.875rem', color: '#92400e' }}>Rules & Validation:</strong>
                          <ul style={{ margin: '0.5rem 0 0 1.5rem', padding: 0, listStyle: 'disc' }}>
                            {rules.map((rule: string, idx: number) => (
                              <li key={idx} style={{ color: '#92400e', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                                {rule}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* System Trigger */}
                      {item.document.system_trigger && (
                        <div style={{ marginBottom: '0.75rem', padding: '0.75rem', background: '#dbeafe', borderRadius: '8px', border: '1px solid #3b82f6' }}>
                          <strong style={{ fontSize: '0.875rem', color: '#1e40af' }}>System Trigger / Outcome:</strong>
                          <p style={{ color: '#1e40af', fontSize: '0.875rem', margin: '0.5rem 0 0 0' }}>
                            {item.document.system_trigger}
                          </p>
                        </div>
                      )}

                      {/* Upload Section */}
                      <>
                        <div style={{ marginTop: '1rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid var(--border)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                            <strong style={{ fontSize: '0.875rem' }}>
                              Uploaded Documents ({item.submission ? 1 : 0})
                            </strong>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => setEditingItem(item)}
                              style={{ fontSize: '0.8rem' }}
                            >
                              + Upload Document
                            </button>
                          </div>
                          {item.submission && (
                            <div style={{ display: 'grid', gap: '0.5rem' }}>
                              <div style={{ padding: '0.75rem', background: 'white', borderRadius: '6px', border: '1px solid var(--border-light)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                  <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                      <strong style={{ fontSize: '0.875rem' }}>
                                        {item.submission.file_url === 'manual' ? 'No file uploaded' : item.submission.file_name}
                                      </strong>
                                      {item.submission.compliance_status && (
                                        <span style={{
                                          padding: '0.125rem 0.5rem',
                                          borderRadius: '12px',
                                          fontSize: '0.7rem',
                                          backgroundColor: ['Yes', 'Completed'].includes(item.submission.compliance_status) ? '#10b981' :
                                            (['No', 'In Progress'].includes(item.submission.compliance_status)) ? '#facc15' :
                                              ['Pending', 'N/A', 'Not Applicable'].includes(item.submission.compliance_status) ? '#94a3b8' : '#94a3b8',
                                          color: 'white',
                                          fontWeight: '600'
                                        }}>
                                          {['Yes', 'Completed'].includes(item.submission.compliance_status) ? 'Completed' : item.submission.compliance_status}
                                        </span>
                                      )}
                                    </div>
                                    <p style={{ fontSize: '0.75rem', color: 'var(--muted)', margin: '0.25rem 0 0 0' }}>
                                      Updated {new Date(item.submission.uploaded_at).toLocaleDateString('en-IN', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric'
                                      })}
                                      {item.submission.expiry_date && ` • Valid till: ${new Date(item.submission.expiry_date).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}`}
                                    </p>
                                  </div>
                                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    {item.submission.file_url !== 'manual' && (
                                      <>
                                        <a
                                          href={item.submission.file_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="btn btn-sm btn-outline"
                                          style={{ fontSize: '0.8rem' }}
                                          onClick={async (e) => {
                                            e.preventDefault();
                                            const signedUrl = await getSignedUrl(item.submission!.file_url, 'company-documents');
                                            window.open(signedUrl, '_blank');
                                          }}
                                        >
                                          View
                                        </a>
                                        <button
                                          onClick={async () => {
                                            await handleDownload(item.submission!.file_url, item.submission!.file_name, 'company-documents');
                                          }}
                                          className="btn btn-sm btn-outline"
                                          style={{ fontSize: '0.8rem' }}
                                        >
                                          Download
                                        </button>
                                      </>
                                    )}
                                    <button
                                      onClick={() => setEditingItem(item)}
                                      className="btn btn-sm btn-outline"
                                      style={{ fontSize: '0.8rem' }}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      className="btn btn-sm"
                                      style={{
                                        fontSize: '0.8rem',
                                        backgroundColor: '#ef4444',
                                        color: 'white',
                                        border: 'none'
                                      }}
                                      onClick={async (e) => {
                                        e.preventDefault();
                                        if (!item.submission) return;
                                        if (!window.confirm('Are you sure you want to delete this document?')) return;
                                        setIsSubmitting(true);
                                        try {
                                          // Delete from storage if not manual
                                          const filePath = item.submission.file_url;
                                          if (filePath !== 'manual') {
                                            const bucket = filePath.includes('/ngo-documents/') || filePath.includes('ngo-documents/') ? 'ngo-documents' : 'company-documents';
                                            let pathInBucket: string | null = null;

                                            if (filePath.includes('/storage/v1/object/public/')) {
                                              const parts = filePath.split(`/storage/v1/object/public/${bucket}/`);
                                              if (parts.length > 1) {
                                                pathInBucket = parts[1].split('?')[0];
                                              }
                                            } else if (filePath.includes('/storage/v1/object/sign/')) {
                                              const parts = filePath.split(`/storage/v1/object/sign/${bucket}/`);
                                              if (parts.length > 1) {
                                                pathInBucket = parts[1].split('?')[0];
                                              }
                                            }

                                            if (pathInBucket) {
                                              const { error: storageError } = await supabase.storage
                                                .from(bucket)
                                                .remove([decodeURIComponent(pathInBucket)]);
                                              if (storageError) {
                                                console.warn('Storage delete warning (continuing):', storageError.message);
                                              }
                                            }
                                          }

                                          // Delete from database — use .select() to verify it actually deleted
                                          const { data: deletedRows, error: deleteError } = await supabase
                                            .from('document_uploads')
                                            .delete()
                                            .eq('id', item.submission.id)
                                            .select('id');

                                          if (deleteError) throw deleteError;

                                          if (!deletedRows || deletedRows.length === 0) {
                                            // RLS or policy blocked the delete — throw a real error
                                            throw new Error('Permission denied: you do not have access to delete this document. Please contact your administrator.');
                                          }

                                          // ✅ Optimistic immediate state update — remove from UI right away
                                          const deletedId = item.submission.id;
                                          setCompanyChecklist(prev => prev.map(ci =>
                                            ci.submission?.id === deletedId ? { ...ci, submission: null, status: 'Not Submitted', allSubmissions: [] } : ci
                                          ));
                                          setNgoChecklist(prev => prev.map(ci =>
                                            ci.submission?.id === deletedId ? { ...ci, submission: null, status: 'Not Submitted', allSubmissions: [] } : ci
                                          ));
                                          setChecklist(prev => prev.map(ci =>
                                            ci.submission?.id === deletedId ? { ...ci, submission: null, status: 'Not Submitted', allSubmissions: [] } : ci
                                          ));

                                          // Refetch in background to sync
                                          if (companyId) fetchData(companyId);
                                        } catch (err: any) {
                                          console.error('Error deleting document:', err);
                                          alert(err.message || 'Failed to delete document');
                                        } finally {
                                          setIsSubmitting(false);
                                        }
                                      }}
                                      disabled={isSubmitting}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </div>
                                {item.submission.notes && (
                                  <div style={{ marginTop: '0.5rem', padding: '0.75rem', background: '#f8fafb', borderRadius: '4px', border: '1px solid var(--border)' }}>
                                    <strong style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Remarks:</strong>
                                    <p style={{ fontSize: '0.875rem', color: 'var(--text)', margin: '0.25rem 0 0 0' }}>
                                      {item.submission.notes}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    </div>
                  );
                })}

                {/* Document Requests logic removed */}
              </div>
            )
          ) : activeSection === 'ngo' ? (
            filteredNgoChecklist.length === 0 ? (
              <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
                <p style={{ color: 'var(--muted)' }}>No NGO compliance documents found.</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '1.5rem' }}>
                {filteredNgoChecklist.map((item) => {
                  const dataFields = typeof item.document.data_fields === 'string'
                    ? JSON.parse(item.document.data_fields || '[]')
                    : (item.document.data_fields || []);
                  const rules = typeof item.document.rules === 'string'
                    ? JSON.parse(item.document.rules || '[]')
                    : (item.document.rules || []);

                  // Get NGO name for each upload
                  const getNgoName = (ngoId: string) => {
                    const ngo = ngos.find(n => n.id === ngoId);
                    return ngo?.legal_name || 'Unknown NGO';
                  };

                  return (
                    <div key={`${item.document.id}-${item.assignment?.ngo_id || 'unknown'}`} className="card" style={{ position: 'relative', paddingLeft: '3rem' }}>
                      {/* Step Number Badge */}
                      <div style={{
                        position: 'absolute',
                        left: '1rem',
                        top: '1rem',
                        width: '2rem',
                        height: '2rem',
                        borderRadius: '50%',
                        background: 'var(--primary)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '600',
                        fontSize: '0.875rem'
                      }}>
                        {item.document.display_order || item.document.step_number}
                      </div>

                      {/* Document Header */}
                      <div style={{ marginBottom: '0.75rem' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                          <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: 'var(--text)' }}>
                            {item.document.document_name} ({getNgoName(item.assignment?.ngo_id || '')})
                          </h3>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            backgroundColor: '#10b98115',
                            color: '#10b981',
                            fontWeight: '600'
                          }}>
                            NGO
                          </span>

                          {/* Action Type Badge Removed Here */}

                        </div>
                      </div>

                      {/* Description */}
                      {item.document.description && (
                        <p style={{ color: 'var(--text-secondary)', marginBottom: '0.75rem', lineHeight: '1.6' }}>
                          {item.document.description}
                        </p>
                      )}

                      {/* Guiding Document */}
                      {item.document.guiding_doc_url && (
                        <div style={{ marginBottom: '0.75rem', padding: '0.75rem', background: '#ecfdf5', borderRadius: '8px', border: '1px solid #10b981' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <strong style={{ fontSize: '0.875rem', color: '#065f46' }}>📄 Guiding Document:</strong>
                              <p style={{ fontSize: '0.75rem', color: '#047857', margin: '0.25rem 0 0 0' }}>
                                {item.document.guiding_doc_filename || 'Guideline Document'}
                              </p>
                            </div>
                            <a
                              href={item.document.guiding_doc_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm"
                              style={{
                                fontSize: '0.8rem',
                                backgroundColor: '#10b981',
                                color: 'white',
                                border: 'none'
                              }}
                            >
                              View Guide
                            </a>
                          </div>
                        </div>
                      )}

                      {/* Responsibility */}
                      {item.document.responsibility && (
                        <div style={{ marginBottom: '0.75rem' }}>
                          <strong style={{ fontSize: '0.875rem' }}>Responsibility:</strong>
                          <span style={{ marginLeft: '0.5rem', color: 'var(--primary)', fontWeight: '500' }}>
                            {item.document.responsibility}
                          </span>
                        </div>
                      )}

                      {/* Data Fields */}
                      {Array.isArray(dataFields) && dataFields.length > 0 && (
                        <div style={{ marginBottom: '0.75rem' }}>
                          <strong style={{ fontSize: '0.875rem' }}>Data Fields:</strong>
                          <ul style={{ margin: '0.5rem 0 0 1.5rem', padding: 0, listStyle: 'disc' }}>
                            {dataFields.map((field: string, idx: number) => (
                              <li key={idx} style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                                {field}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Rules */}
                      {Array.isArray(rules) && rules.length > 0 && (
                        <div style={{ marginBottom: '0.75rem', padding: '0.75rem', background: '#fef3c7', borderRadius: '8px', border: '1px solid #f59e0b' }}>
                          <strong style={{ fontSize: '0.875rem', color: '#92400e' }}>Rules & Validation:</strong>
                          <ul style={{ margin: '0.5rem 0 0 1.5rem', padding: 0, listStyle: 'disc' }}>
                            {rules.map((rule: string, idx: number) => (
                              <li key={idx} style={{ color: '#92400e', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                                {rule}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* System Trigger */}
                      {item.document.system_trigger && (
                        <div style={{ marginBottom: '0.75rem', padding: '0.75rem', background: '#dbeafe', borderRadius: '8px', border: '1px solid #3b82f6' }}>
                          <strong style={{ fontSize: '0.875rem', color: '#1e40af' }}>System Trigger / Outcome:</strong>
                          <p style={{ color: '#1e40af', fontSize: '0.875rem', margin: '0.5rem 0 0 0' }}>
                            {item.document.system_trigger}
                          </p>
                        </div>
                      )}

                      {/* NGO Document Submissions */}
                      <div style={{ marginTop: '1rem', padding: '1rem', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #3b82f6' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                          <strong style={{ fontSize: '0.875rem', color: '#1e40af' }}>
                            NGO Document Submissions
                          </strong>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => setEditingItem(item)}
                            style={{ fontSize: '0.8rem', backgroundColor: '#3b82f6', color: 'white', border: 'none' }}
                          >
                            + Upload Document
                          </button>
                        </div>
                        {(() => {
                          // Show only uploads from the specific NGO this card represents
                          const ngoUploads = item.allSubmissions
                            ? item.allSubmissions.filter((upload: any) => upload.ngo_id === item.assignment?.ngo_id)
                            : [];

                          if (ngoUploads.length > 0) {
                            // Sort and show only the latest upload
                            const latestUpload = [...ngoUploads].sort((a, b) =>
                              new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime()
                            )[0];

                            return (
                              <div style={{ marginTop: '0.75rem', display: 'grid', gap: '0.5rem' }}>
                                <div key={latestUpload.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: 'white', borderRadius: '6px', border: '1px solid var(--border)' }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                                      <strong style={{ fontSize: '0.875rem' }}>{latestUpload.file_name}</strong>
                                      {latestUpload.notes && (
                                        <span style={{
                                          padding: '0.125rem 0.5rem',
                                          borderRadius: '12px',
                                          fontSize: '0.7rem',
                                          backgroundColor: '#f59e0b',
                                          color: 'white',
                                          fontWeight: '600'
                                        }}>
                                          Changes Requested
                                        </span>
                                      )}
                                    </div>
                                    <p style={{ fontSize: '0.75rem', color: 'var(--muted)', margin: '0.25rem 0 0 0' }}>
                                      Uploaded on {new Date(latestUpload.uploaded_at).toLocaleDateString('en-IN', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric'
                                      })}
                                    </p>
                                    {latestUpload.notes && (
                                      <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#f8fafb', borderRadius: '4px', border: '1px solid var(--border)' }}>
                                        <strong style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Company Feedback:</strong>
                                        <p style={{ fontSize: '0.75rem', color: 'var(--text)', margin: '0.25rem 0 0 0' }}>
                                          {latestUpload.notes}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <a
                                      href={latestUpload.file_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="btn btn-sm btn-outline"
                                      style={{ fontSize: '0.8rem' }}
                                      onClick={async (e) => {
                                        e.preventDefault();
                                        const signedUrl = await getSignedUrl(latestUpload.file_url, 'ngo-documents');
                                        window.open(signedUrl, '_blank');
                                      }}
                                    >
                                      View
                                    </a>
                                    <button
                                      onClick={async () => {
                                        await handleDownload(latestUpload.file_url, latestUpload.file_name, 'ngo-documents');
                                      }}
                                      className="btn btn-sm btn-outline"
                                      style={{ fontSize: '0.8rem' }}
                                    >
                                      Download
                                    </button>
                                    <button
                                      className="btn btn-sm"
                                      style={{
                                        fontSize: '0.8rem',
                                        backgroundColor: latestUpload.notes ? '#f59e0b' : '#3b82f6',
                                        color: 'white',
                                        border: 'none'
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setFeedbackModal({
                                          uploadId: latestUpload.id,
                                          fileName: latestUpload.file_name,
                                          currentFeedback: latestUpload.notes || null
                                        });
                                        setFeedbackText(latestUpload.notes || '');
                                      }}
                                      disabled={isSubmitting}
                                    >
                                      {latestUpload.notes ? 'Update Feedback' : 'Request Changes'}
                                    </button>
                                    <button
                                      onClick={() => setEditingItem(item)}
                                      className="btn btn-sm btn-outline"
                                      style={{ fontSize: '0.8rem', borderColor: '#3b82f6', color: '#3b82f6' }}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      className="btn btn-sm"
                                      style={{
                                        fontSize: '0.8rem',
                                        backgroundColor: '#ef4444',
                                        color: 'white',
                                        border: 'none'
                                      }}
                                      onClick={async (e) => {
                                        e.preventDefault();
                                        if (!window.confirm('Are you sure you want to delete this document?')) return;
                                        setIsSubmitting(true);
                                        try {
                                          const filePath = latestUpload.file_url;
                                          if (filePath !== 'manual') {
                                            const bucket = filePath.includes('ngo-documents') ? 'ngo-documents' : 'company-documents';
                                            let pathInBucket: string | null = null;
                                            if (filePath.includes('/storage/v1/object/public/')) {
                                              pathInBucket = filePath.split(`/storage/v1/object/public/${bucket}/`)[1]?.split('?')[0];
                                            } else if (filePath.includes('/storage/v1/object/sign/')) {
                                              pathInBucket = filePath.split(`/storage/v1/object/sign/${bucket}/`)[1]?.split('?')[0];
                                            }
                                            if (pathInBucket) {
                                              await supabase.storage.from(bucket).remove([decodeURIComponent(pathInBucket)]);
                                            }
                                          }
                                          const { data: cols, error: delErr } = await supabase.from('document_uploads').delete().eq('id', latestUpload.id).select('id');
                                          if (delErr) throw delErr;
                                          if (!cols || cols.length === 0) throw new Error('Permission denied.');
                                          if (companyId) fetchData(companyId);
                                        } catch (err: any) {
                                          alert(err.message || 'Failed to delete');
                                        } finally {
                                          setIsSubmitting(false);
                                        }
                                      }}
                                      disabled={isSubmitting}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          } else {
                            return (
                              <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: 'var(--muted)' }}>
                                No documents uploaded yet by {getNgoName(item.assignment?.ngo_id || '')}.
                              </p>
                            );
                          }
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : null}
        </div>

        {/* Upload Modal */}
        {editingItem && (
          <div className="modal-overlay" onClick={() => setEditingItem(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
              <div style={{
                marginBottom: '1.5rem',
                paddingBottom: '1rem',
                borderBottom: '2px solid var(--border-light)'
              }}>
                <h2 style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: 'var(--text)',
                  margin: 0,
                  marginBottom: '0.25rem'
                }}>
                  Compliance Status & Remarks
                </h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>
                  {editingItem?.document.document_name}
                </p>
              </div>
              <form onSubmit={async (e) => {
                e.preventDefault();
                if (!editingItem || !companyId) return;

                const formData = new FormData(e.currentTarget);
                const complianceStatus = formData.get('compliance_status') as string;
                const remarks = formData.get('remarks') as string;
                const validity = formData.get('validity') as string;
                const file = formData.get('file') as File;

                setIsSubmitting(true);
                try {
                  let fileUrl = editingItem.submission?.file_url || '';
                  let fileName = editingItem.submission?.file_name || '';

                  if (file && file.size > 0) {
                    const storagePath = `${companyId}/compliance/${editingItem.document.id}_${Date.now()}_${file.name}`;
                    const { error: uploadError } = await supabase.storage
                      .from('company-documents')
                      .upload(storagePath, file);
                    if (uploadError) throw uploadError;

                    const { data: signedData } = await supabase.storage
                      .from('company-documents')
                      .createSignedUrl(storagePath, 31536000); // 1 year expiry

                    fileUrl = signedData?.signedUrl || '';
                    fileName = file.name;
                  } else if (!fileUrl) {
                    fileUrl = 'manual';
                    fileName = 'No file uploaded';
                  }

                  let assignIdToUse = (editingItem.assignment?.id && !String(editingItem.assignment.id).startsWith('auto-') && !String(editingItem.assignment.id).startsWith('orphan-'))
                    ? editingItem.assignment.id
                    : null;

                  if (!assignIdToUse) {
                    let query = supabase
                      .from('document_assignments')
                      .select('id')
                      .eq('company_id', companyId)
                      .eq('document_repository_id', editingItem.document.id);

                    if (editingItem.assignment?.entity_id) {
                      query = query.eq('entity_id', editingItem.assignment.entity_id);
                    } else if (editingItem.assignment?.ngo_id) {
                      query = query.eq('ngo_id', editingItem.assignment.ngo_id);
                    } else {
                      query = query.is('entity_id', null).is('ngo_id', null);
                    }

                    const { data: existing } = await query;

                    if (existing && existing.length > 0) {
                      assignIdToUse = existing[0].id;
                    } else {
                      const { data: newAssign, error: assignErr } = await supabase
                        .from('document_assignments')
                        .insert({
                          document_repository_id: editingItem.document.id,
                          company_id: companyId,
                          entity_id: editingItem.assignment?.entity_id || null,
                          ngo_id: editingItem.assignment?.ngo_id || null,
                          status: 'Pending'
                        })
                        .select()
                        .single();

                      if (assignErr) throw assignErr;
                      assignIdToUse = newAssign.id;
                    }
                  }

                  const submissionData: any = {
                    company_id: companyId,
                    ngo_id: editingItem.assignment?.ngo_id || null,
                    document_repository_id: editingItem.document.id,
                    assignment_id: assignIdToUse,
                    file_name: fileName,
                    file_url: fileUrl,
                    status: 'active',
                    notes: remarks || null,
                    compliance_status: complianceStatus,
                    expiry_date: validity ? validity : null
                  };

                  if (editingItem.submission) {
                    const { error: updateError } = await supabase
                      .from('document_uploads')
                      .update(submissionData)
                      .eq('id', editingItem.submission.id);
                    if (updateError) throw updateError;
                  } else {
                    const { error: insertError } = await supabase
                      .from('document_uploads')
                      .insert(submissionData);
                    if (insertError) throw insertError;
                  }

                  setEditingItem(null);
                  if (companyId) fetchData(companyId);
                } catch (err: any) {
                  console.error('Upload/Status update error:', err);
                  alert(err.message || 'Failed to update compliance status');
                } finally {
                  setIsSubmitting(false);
                }
              }}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Status *
                  </label>
                  <select
                    name="compliance_status"
                    defaultValue={editingItem.submission?.compliance_status || 'Pending'}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: '0.875rem'
                    }}
                  >
                    <option value="Completed">Completed</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Pending">Pending</option>
                    <option value="Not Applicable">Not Applicable</option>
                  </select>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Upload File
                  </label>
                  <input
                    type="file"
                    id="compliance-file-input"
                    name="file"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: '0.875rem'
                    }}
                  />
                  {editingItem.submission?.file_url && editingItem.submission.file_url !== 'manual' && (
                    <p style={{ fontSize: '0.75rem', color: 'var(--primary)', marginTop: '0.5rem' }}>
                      Currently uploaded: {editingItem.submission.file_name}
                    </p>
                  )}
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Validity
                  </label>
                  <input
                    type="date"
                    name="validity"
                    defaultValue={editingItem.submission?.expiry_date || ''}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Remarks / Implementation Notes
                  </label>
                  <textarea
                    name="remarks"
                    defaultValue={editingItem.submission?.notes || ''}
                    placeholder="Add notes about compliance status, verification steps, or manual confirmation details..."
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: '0.875rem',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => setEditingItem(null)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Saving...' : 'Save Status'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Review Modal */}
        {editingItem && editingItem.submission && editingItem.document.role === 'ngo' && (
          <div className="modal-overlay" onClick={() => setEditingItem(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
              <div style={{
                marginBottom: '1.5rem',
                paddingBottom: '1rem',
                borderBottom: '2px solid var(--border-light)'
              }}>
                <h2 style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: 'var(--text)',
                  margin: 0,
                  marginBottom: '0.25rem'
                }}>
                  Review NGO Document
                </h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>
                  {editingItem.document.document_name} - {editingItem.ngo_name}
                </p>
              </div>
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
                  <a
                    href={editingItem.submission.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                    style={{ flex: 1 }}
                    onClick={async (e) => {
                      e.preventDefault();
                      const signedUrl = await getSignedUrl(editingItem.submission!.file_url, 'ngo-documents');
                      window.open(signedUrl, '_blank');
                    }}
                  >
                    View Document
                  </a>
                  <button
                    onClick={async () => {
                      await handleDownload(editingItem.submission!.file_url, editingItem.submission!.file_name, 'ngo-documents');
                    }}
                    className="btn btn-outline"
                    style={{ flex: 1 }}
                  >
                    Download
                  </button>
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Status
                  </label>
                  <select
                    value={editingItem.submission.status}
                    onChange={async (e) => {
                      if (!editingItem?.submission || !companyId) return;
                      const newStatus = e.target.value;
                      setIsSubmitting(true);
                      try {
                        const { error } = await supabase
                          .from('document_uploads')
                          .update({ status: newStatus })
                          .eq('id', editingItem.submission!.id);
                        if (error) throw error;
                        setEditingItem(null);
                        if (companyId) fetchData(companyId);
                      } catch (err: any) {
                        console.error('Update error:', err);
                        alert(err.message || 'Failed to update status');
                      } finally {
                        setIsSubmitting(false);
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: '0.875rem'
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setEditingItem(null)}
                  disabled={isSubmitting}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal removed */}

        {/* Feedback/Request Changes Modal */}
        {feedbackModal && (
          <div className="modal-overlay" onClick={() => {
            setFeedbackModal(null);
            setFeedbackText('');
          }}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
              <div style={{
                marginBottom: '1.5rem',
                paddingBottom: '1rem',
                borderBottom: '2px solid var(--border-light)'
              }}>
                <h2 style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: 'var(--text)',
                  margin: 0,
                  marginBottom: '0.25rem'
                }}>
                  Request Changes / Provide Feedback
                </h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>
                  {feedbackModal.fileName}
                </p>
              </div>
              <form onSubmit={async (e) => {
                e.preventDefault();
                if (!feedbackModal || !companyId) return;
                setIsSubmitting(true);
                try {
                  const { error } = await supabase
                    .from('document_uploads')
                    .update({
                      notes: feedbackText.trim() || null,
                      status: feedbackText.trim() ? 'active' : 'active' // Keep status as active, use notes for feedback
                    })
                    .eq('id', feedbackModal.uploadId);

                  if (error) throw error;

                  alert('Feedback submitted successfully! The NGO will see this feedback.');
                  setFeedbackModal(null);
                  setFeedbackText('');
                  if (companyId) await fetchData(companyId);
                } catch (err: any) {
                  console.error('Error submitting feedback:', err);
                  alert(err.message || 'Failed to submit feedback');
                } finally {
                  setIsSubmitting(false);
                }
              }}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Feedback / Changes Requested
                  </label>
                  <textarea
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    placeholder="Describe what changes are needed or provide feedback on this document..."
                    rows={6}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: '0.875rem',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                  {feedbackModal.currentFeedback && (
                    <div style={{
                      marginTop: '0.75rem',
                      padding: '0.75rem',
                      background: '#fef3c7',
                      borderRadius: '8px',
                      border: '1px solid #f59e0b'
                    }}>
                      <strong style={{ fontSize: '0.875rem', color: '#92400e' }}>Previous Feedback:</strong>
                      <p style={{ fontSize: '0.875rem', color: '#92400e', margin: '0.5rem 0 0 0' }}>
                        {feedbackModal.currentFeedback}
                      </p>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => {
                      setFeedbackModal(null);
                      setFeedbackText('');
                    }}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Custom Document Modal */}
        {showAddDocumentModal && (
          <div className="modal-overlay" onClick={() => {
            setShowAddDocumentModal(false);
            setNewDocumentForm({
              document_name: '',
              description: '',
              selected_entities: [],
              selected_ngos: [],
              assign_to_admin: false,
              due_date: '',
              template_file: null
            });
          }}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
              <div style={{
                marginBottom: '1.5rem',
                paddingBottom: '1rem',
                borderBottom: '2px solid var(--border-light)'
              }}>
                <h2 style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: 'var(--text)',
                  margin: 0,
                  marginBottom: '0.25rem'
                }}>
                  Add Custom Document
                </h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>
                  Create a new document that NGOs can view
                </p>
              </div>
              <form onSubmit={async (e) => {
                e.preventDefault();
                if (!companyId) return;
                setIsSubmitting(true);
                try {
                  // Get the highest step_number for company documents (custom documents use >= 100)
                  // We'll use sequential numbering starting from 100 for custom documents
                  const { data: existingDocs } = await supabase
                    .from('document_repository')
                    .select('step_number')
                    .eq('role', 'company')
                    .gte('step_number', 100)
                    .order('step_number', { ascending: false })
                    .limit(1);

                  // Calculate next step number: start from 100 and increment sequentially
                  // If there are existing custom docs, find the next available number
                  let nextStepNumber = 100;
                  if (existingDocs && existingDocs.length > 0 && existingDocs[0].step_number) {
                    const highestStep = existingDocs[0].step_number;
                    // Increment from the highest, but cap at 999 to keep numbers reasonable
                    // If we hit 999, we'll find the next available gap starting from 100
                    if (highestStep >= 999) {
                      // Find the next available number between 100-999
                      const { data: allCustomDocs } = await supabase
                        .from('document_repository')
                        .select('step_number')
                        .eq('role', 'company')
                        .gte('step_number', 100)
                        .lt('step_number', 999)
                        .order('step_number', { ascending: true });

                      if (allCustomDocs && allCustomDocs.length > 0) {
                        // Find first gap, or use highest + 1 if no gaps
                        let foundGap = false;
                        for (let i = 100; i < 999; i++) {
                          if (!allCustomDocs.some((doc: any) => doc.step_number === i)) {
                            nextStepNumber = i;
                            foundGap = true;
                            break;
                          }
                        }
                        if (!foundGap) {
                          // No gaps found, use highest + 1
                          const highest = Math.max(...allCustomDocs.map((doc: any) => doc.step_number || 100));
                          nextStepNumber = Math.min(highest + 1, 999);
                        }
                      } else {
                        nextStepNumber = 100;
                      }
                    } else {
                      nextStepNumber = highestStep + 1;
                    }
                  }

                  let fileUrl = null;
                  if (newDocumentForm.template_file) {
                    const fileExt = newDocumentForm.template_file.name.split('.').pop();
                    const fileName = `${companyId}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
                    const { error: uploadError } = await supabase.storage
                      .from('company-documents')
                      .upload(fileName, newDocumentForm.template_file);

                    if (uploadError) throw uploadError;

                    const { data: { publicUrl } } = supabase.storage
                      .from('company-documents')
                      .getPublicUrl(fileName);
                    fileUrl = publicUrl;
                  }

                  // Create the document in document_repository (default category: compliance)
                  const { data: newDoc, error: docError } = await supabase
                    .from('document_repository')
                    .insert({
                      category: 'compliance', // Default category for company documents
                      role: 'company',
                      step_number: nextStepNumber,
                      document_name: newDocumentForm.document_name,
                      action_type: 'View & Download',
                      description: newDocumentForm.description || `Custom document: ${newDocumentForm.document_name}`,
                      data_fields: [] as any,
                      responsibility: 'Company',
                      rules: [] as any,
                      display_order: nextStepNumber,
                      is_active: true,
                      visible_to_company: true,
                      visible_to_ngo: false, // Will be controlled by visibility table
                      can_company_upload: true,
                      can_ngo_upload: false
                    })
                    .select()
                    .single();

                  if (docError) throw docError;

                  // If a guiding file was uploaded, save it as a document_upload record
                  if (newDoc && fileUrl) {
                    await supabase.from('document_uploads').insert({
                      document_repository_id: newDoc.id,
                      company_id: companyId,
                      file_url: fileUrl,
                      file_name: newDocumentForm.template_file?.name || 'Guiding Document',
                      status: 'active',
                      upload_type: 'guiding_document',
                    });
                  }

                  // Create Assignments for the new document
                  if (newDoc) {
                    const assignmentRecords: any[] = [];

                    // Add Corporate assignment
                    if (newDocumentForm.assign_to_admin) {
                      assignmentRecords.push({
                        document_repository_id: newDoc.id,
                        company_id: companyId,
                        entity_id: null,
                        ngo_id: null,
                        status: 'Pending',
                        due_date: newDocumentForm.due_date || null
                      });
                    }

                    // Add Company assignments
                    newDocumentForm.selected_entities.forEach(entityId => {
                      assignmentRecords.push({
                        document_repository_id: newDoc.id,
                        company_id: companyId,
                        entity_id: entityId,
                        ngo_id: null,
                        status: 'Pending',
                        due_date: newDocumentForm.due_date || null
                      });
                    });

                    // Add NGO assignments
                    newDocumentForm.selected_ngos.forEach(ngoId => {
                      assignmentRecords.push({
                        document_repository_id: newDoc.id,
                        company_id: companyId,
                        entity_id: null,
                        ngo_id: ngoId,
                        status: 'Pending',
                        due_date: newDocumentForm.due_date || null
                      });
                    });

                    if (assignmentRecords.length > 0) {
                      const { error: assignError } = await supabase
                        .from('document_assignments')
                        .insert(assignmentRecords);

                      if (assignError) throw assignError;
                    }
                  }

                  alert('Custom document created successfully!');
                  setShowAddDocumentModal(false);
                  setNewDocumentForm({
                    document_name: '',
                    description: '',
                    selected_entities: [],
                    selected_ngos: [],
                    assign_to_admin: false,
                    due_date: '',
                    template_file: null
                  });
                  if (companyId) await fetchData(companyId);
                } catch (err: any) {
                  console.error('Error creating custom document:', err);
                  alert(err.message || 'Failed to create custom document');
                } finally {
                  setIsSubmitting(false);
                }
              }}>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Document Name *
                  </label>
                  <input
                    type="text"
                    value={newDocumentForm.document_name}
                    onChange={(e) => setNewDocumentForm({ ...newDocumentForm, document_name: e.target.value })}
                    required
                    placeholder="e.g., Company Policy Document"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Description
                  </label>
                  <textarea
                    value={newDocumentForm.description}
                    onChange={(e) => setNewDocumentForm({ ...newDocumentForm, description: e.target.value })}
                    placeholder="Describe what this document is for..."
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: '0.875rem',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Due Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={newDocumentForm.due_date}
                    onChange={(e) => setNewDocumentForm({ ...newDocumentForm, due_date: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Guiding Document (Optional)
                  </label>
                  <div style={{
                    border: '1px dashed var(--border)',
                    borderRadius: 'var(--radius-md)',
                    padding: '1rem',
                    textAlign: 'center',
                    background: '#f9fafb'
                  }}>
                    <input
                      type="file"
                      id="guiding-doc-upload"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          setNewDocumentForm({ ...newDocumentForm, template_file: e.target.files[0] });
                        }
                      }}
                    />
                    <label
                      htmlFor="guiding-doc-upload"
                      style={{
                        cursor: 'pointer',
                        color: 'var(--primary)',
                        fontWeight: 600,
                        display: 'inline-block'
                      }}
                    >
                      {newDocumentForm.template_file ? newDocumentForm.template_file.name : 'Click to select a file'}
                    </label>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0.25rem 0 0 0' }}>
                      Upload a template or guidelines for this document
                    </p>
                  </div>
                </div>

                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Assign To *
                  </label>

                  {/* Assign to Corporate/Main Account */}
                  <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '6px', border: '1px solid var(--border)', marginBottom: '1rem' }}>
                    <strong style={{ fontSize: '0.75rem', display: 'block', marginBottom: '0.5rem' }}>
                      Main Company / Corporate:
                    </strong>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={newDocumentForm.assign_to_admin}
                        onChange={(e) => {
                          e.stopPropagation();
                          setNewDocumentForm(prev => ({ ...prev, assign_to_admin: e.target.checked }));
                        }}
                        style={{ cursor: 'pointer', margin: 0 }}
                      />
                      <span
                        style={{ fontSize: '0.875rem', flex: 1 }}
                        onClick={() => {
                          setNewDocumentForm(prev => ({ ...prev, assign_to_admin: !prev.assign_to_admin }));
                        }}
                      >
                        {companyName || 'Company Corporate'}
                      </span>
                    </div>
                  </div>

                  {/* Assign to Entities - Only show if not single company */}
                  {companyProfile?.setup_type !== 'single' && (
                    <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '6px', border: '1px solid var(--border)', marginBottom: '1rem' }}>
                      <strong style={{ fontSize: '0.75rem', display: 'block', marginBottom: '0.5rem' }}>
                        {companyProfile?.setup_type === 'single' ? 'Business Units:' : 'Internal Companies:'}
                      </strong>
                      {companyEntities.length === 0 ? (
                        <p style={{ fontSize: '0.75rem', color: 'var(--muted)', margin: 0 }}>No business units added yet.</p>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.5rem' }}>
                          {companyEntities.map(entity => (
                            <div key={`form-entity-${entity.id}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem' }}>
                              <input
                                type="checkbox"
                                checked={newDocumentForm.selected_entities.includes(entity.id)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  if (e.target.checked) {
                                    setNewDocumentForm(prev => ({ ...prev, selected_entities: [...prev.selected_entities, entity.id] }));
                                  } else {
                                    setNewDocumentForm(prev => ({ ...prev, selected_entities: prev.selected_entities.filter(id => id !== entity.id) }));
                                  }
                                }}
                                style={{ cursor: 'pointer', margin: 0 }}
                              />
                              <span
                                style={{ fontSize: '0.875rem', flex: 1 }}
                                onClick={() => {
                                  if (newDocumentForm.selected_entities.includes(entity.id)) {
                                    setNewDocumentForm(prev => ({ ...prev, selected_entities: prev.selected_entities.filter(id => id !== entity.id) }));
                                  } else {
                                    setNewDocumentForm(prev => ({ ...prev, selected_entities: [...prev.selected_entities, entity.id] }));
                                  }
                                }}
                              >
                                {entity.name}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Assign to NGOs */}
                  <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '6px', border: '1px solid var(--border)' }}>
                    <strong style={{ fontSize: '0.75rem', display: 'block', marginBottom: '0.5rem' }}>
                      External NGOs:
                    </strong>
                    {ngos.length === 0 ? (
                      <p style={{ fontSize: '0.75rem', color: 'var(--muted)', margin: 0 }}>No NGOs linked to this company yet.</p>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.5rem' }}>
                        {ngos.map(ngo => (
                          <div key={`form-ngo-${ngo.id}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.5rem' }}>
                            <input
                              type="checkbox"
                              checked={newDocumentForm.selected_ngos.includes(ngo.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (e.target.checked) {
                                  setNewDocumentForm(prev => ({ ...prev, selected_ngos: [...prev.selected_ngos, ngo.id] }));
                                } else {
                                  setNewDocumentForm(prev => ({ ...prev, selected_ngos: prev.selected_ngos.filter(id => id !== ngo.id) }));
                                }
                              }}
                              style={{ cursor: 'pointer', margin: 0 }}
                            />
                            <span
                              style={{ fontSize: '0.875rem', flex: 1 }}
                              onClick={() => {
                                if (newDocumentForm.selected_ngos.includes(ngo.id)) {
                                  setNewDocumentForm(prev => ({ ...prev, selected_ngos: prev.selected_ngos.filter(id => id !== ngo.id) }));
                                } else {
                                  setNewDocumentForm(prev => ({ ...prev, selected_ngos: [...prev.selected_ngos, ngo.id] }));
                                }
                              }}
                            >
                              {ngo.legal_name}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => {
                      setShowAddDocumentModal(false);
                      setNewDocumentForm({
                        document_name: '',
                        description: '',
                        selected_entities: [],
                        selected_ngos: [],
                        assign_to_admin: false,
                        due_date: '',
                        template_file: null
                      });
                    }}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting || (newDocumentForm.selected_entities.length === 0 && newDocumentForm.selected_ngos.length === 0 && !newDocumentForm.assign_to_admin)}
                  >
                    {isSubmitting ? 'Creating...' : 'Create Document'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyCompliance;
